import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Divider,
  Alert,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  AccountCircle,
  Edit,
  Save,
  Cancel,
  Visibility,
  VisibilityOff,
  AccountBalance,
  CreditCard,
  RequestPage,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useBanking } from '../context/BankingContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const { accounts, creditCards, loanApplications, loans } = useBanking();
  
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    mobileNumber: user?.mobileNumber || '',
    street: user?.address.street || '',
    city: user?.address.city || '',
    state: user?.address.state || '',
    zipCode: user?.address.zipCode || '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
    setIsEditing(false);
    setIsChangingPassword(false);
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handlePasswordChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setProfileData({
      fullName: user?.fullName || '',
      mobileNumber: user?.mobileNumber || '',
      street: user?.address.street || '',
      city: user?.address.city || '',
      state: user?.address.state || '',
      zipCode: user?.address.zipCode || '',
    });
    setError('');
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');

    try {
      const updatedData = {
        fullName: profileData.fullName,
        mobileNumber: profileData.mobileNumber,
        address: {
          street: profileData.street,
          city: profileData.city,
          state: profileData.state,
          zipCode: profileData.zipCode,
        },
      };

      const success = await updateProfile(updatedData);
      if (success) {
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
      } else {
        setError('Failed to update profile');
      }
    } catch (err) {
      setError('An error occurred while updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setLoading(true);
    setError('');

    if (passwordData.currentPassword !== user?.password) {
      setError('Current password is incorrect');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const success = await updateProfile({ password: passwordData.newPassword });
      if (success) {
        setSuccess('Password changed successfully!');
        setIsChangingPassword(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        setError('Failed to change password');
      }
    } catch (err) {
      setError('An error occurred while changing password');
    } finally {
      setLoading(false);
    }
  };

  const totalBalance = accounts.reduce((sum, account) => sum + account.balance, 0);
  const activeCards = creditCards.filter(card => card.status === 'active').length;
  const activeLoans = loans.filter(loan => loan.status === 'active').length;
  const pendingApplications = loanApplications.filter(app => app.status === 'pending').length;

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          User not found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
            <AccountCircle sx={{ fontSize: 40 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {user.fullName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {user.email} • {user.role === 'admin' ? 'Administrator' : 'Customer'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Member since {user.createdAt.toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs">
            <Tab label="Profile Information" />
            <Tab label="Security" />
            <Tab label="Account Summary" />
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Personal Information</Typography>
            {!isEditing ? (
              <Button
                startIcon={<Edit />}
                onClick={handleEditProfile}
                variant="outlined"
              >
                Edit Profile
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<Save />}
                  onClick={handleSaveProfile}
                  variant="contained"
                  disabled={loading}
                >
                  Save
                </Button>
                <Button
                  startIcon={<Cancel />}
                  onClick={handleCancelEdit}
                  variant="outlined"
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profileData.fullName}
                  onChange={handleInputChange('fullName')}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={user.email}
                  disabled
                  variant="filled"
                  helperText="Email cannot be changed"
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  value={profileData.mobileNumber}
                  onChange={handleInputChange('mobileNumber')}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Address Information
            </Typography>
            
            <TextField
              fullWidth
              label="Street Address"
              value={profileData.street}
              onChange={handleInputChange('street')}
              disabled={!isEditing}
              variant={isEditing ? 'outlined' : 'filled'}
            />
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '2 1 200px', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  label="City"
                  value={profileData.city}
                  onChange={handleInputChange('city')}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Box>
              
              <Box sx={{ flex: '1 1 120px', minWidth: '120px' }}>
                {isEditing ? (
                  <FormControl fullWidth>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={profileData.state}
                      label="State"
                      onChange={(e) => setProfileData(prev => ({ ...prev, state: e.target.value }))}
                    >
                      {usStates.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="State"
                    value={profileData.state}
                    disabled
                    variant="filled"
                  />
                )}
              </Box>
              
              <Box sx={{ flex: '1 1 120px', minWidth: '120px' }}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={profileData.zipCode}
                  onChange={handleInputChange('zipCode')}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Box>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          
          {!isChangingPassword ? (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Keep your account secure by using a strong password.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setIsChangingPassword(true)}
              >
                Change Password
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
              <TextField
                fullWidth
                type={showCurrentPassword ? 'text' : 'password'}
                label="Current Password"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange('currentPassword')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    type={showNewPassword ? 'text' : 'password'}
                    label="New Password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange('newPassword')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm New Password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange('confirmPassword')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={loading}
                >
                  Change Password
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsChangingPassword(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    });
                    setError('');
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Account Overview
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccountBalance color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Total Balance
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    ${totalBalance.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Across {accounts.length} account{accounts.length !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CreditCard color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Credit Cards
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="secondary">
                    {activeCards}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active card{activeCards !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <RequestPage color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Active Loans
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="info">
                    {activeLoans}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Loan{activeLoans !== 1 ? 's' : ''} in progress
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <RequestPage color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Pending
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="warning">
                    {pendingApplications}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Application{pendingApplications !== 1 ? 's' : ''} pending
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Recent Accounts
            </Typography>
            {accounts.length > 0 ? (
              accounts.map((account) => (
                <Card key={account.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6">
                          {account.accountType.charAt(0).toUpperCase() + account.accountType.slice(1)} Account
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Account Number: ****{account.accountNumber.slice(-4)}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" color={account.status === 'active' ? 'primary' : 'error'}>
                          ${account.balance.toFixed(2)}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color={account.status === 'active' ? 'success.main' : 'error.main'}
                        >
                          {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography variant="body1" color="text.secondary">
                No accounts found.
              </Typography>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Profile; 